/**
 * Calendar Export Utility
 * Generates ICS files for match events and triggers download.
 * Compatible with Outlook, Apple Calendar, Google Calendar, and other standard calendar apps.
 */

export interface CalendarEvent {
  id: string | number;
  title: string;
  startDate: Date;
  endDate: Date;
  location?: string;
  description?: string;
}

/**
 * Formats a Date to ICS datetime format (UTC): YYYYMMDDTHHmmssZ
 */
function formatDateToICS(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    date.getUTCFullYear().toString() +
    pad(date.getUTCMonth() + 1) +
    pad(date.getUTCDate()) +
    'T' +
    pad(date.getUTCHours()) +
    pad(date.getUTCMinutes()) +
    pad(date.getUTCSeconds()) +
    'Z'
  );
}

/**
 * Escapes special characters in ICS text values
 */
function escapeICSText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/**
 * Generates an ICS file content string for a calendar event
 */
export function generateICS(event: CalendarEvent): string {
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//VoyagePlanner//Game Schedule//DE',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:match-${event.id}@voyageplanner`,
    `DTSTAMP:${formatDateToICS(new Date())}`,
    `DTSTART:${formatDateToICS(event.startDate)}`,
    `DTEND:${formatDateToICS(event.endDate)}`,
    `SUMMARY:${escapeICSText(event.title)}`,
  ];

  if (event.description) {
    lines.push(`DESCRIPTION:${escapeICSText(event.description)}`);
  }

  if (event.location) {
    lines.push(`LOCATION:${escapeICSText(event.location)}`);
  }

  lines.push('END:VEVENT', 'END:VCALENDAR');

  return lines.join('\r\n');
}

/**
 * Triggers a file download in the browser
 */
function downloadFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Exports a match event to a downloadable .ics file
 */
export function exportToCalendar(event: CalendarEvent): void {
  const icsContent = generateICS(event);
  const safeTitle = event.title.replace(/[^a-zA-Z0-9äöüÄÖÜß\s-]/g, '').replace(/\s+/g, '_');
  downloadFile(icsContent, `${safeTitle}.ics`);
}

/**
 * Helper: Creates a CalendarEvent from a Match object (sports matches)
 */
export function matchToCalendarEvent(match: {
  id: number;
  scheduledDate: string;
  date?: string;
  homeClub?: { name: string; stadium?: string; stadiumAddress?: string; city?: string };
  awayClub?: { name: string };
  matchday?: { name: string; season?: { name: string; league?: { name: string } } };
  notes?: string;
}): CalendarEvent {
  const startDate = new Date(match.date || match.scheduledDate);
  const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // +2h default duration

  const homeName = match.homeClub?.name || 'Heim';
  const awayName = match.awayClub?.name || 'Gast';
  const title = `${homeName} vs ${awayName}`;

  const descParts: string[] = [];
  if (match.matchday?.name) descParts.push(`Spieltag: ${match.matchday.name}`);
  if (match.matchday?.season?.name) descParts.push(`Saison: ${match.matchday.season.name}`);
  if (match.matchday?.season?.league?.name) descParts.push(`Liga: ${match.matchday.season.league.name}`);
  if (match.notes) descParts.push(`Notizen: ${match.notes}`);

  const location = match.homeClub?.stadiumAddress || match.homeClub?.stadium || match.homeClub?.city;

  return {
    id: match.id,
    title,
    startDate,
    endDate,
    location,
    description: descParts.length > 0 ? descParts.join('\n') : undefined,
  };
}

/**
 * Helper: Creates a CalendarEvent from a LocalMatch object
 */
export function localMatchToCalendarEvent(match: {
  id: number;
  homeTeam: string;
  awayTeam: string;
  scheduledDate: string;
  location?: string;
  duration?: number;
  notes?: string;
  localMatchday?: { name: string };
}): CalendarEvent {
  const startDate = new Date(match.scheduledDate);
  const durationMs = (match.duration || 120) * 60 * 1000;
  const endDate = new Date(startDate.getTime() + durationMs);

  const title = `${match.homeTeam} vs ${match.awayTeam}`;

  const descParts: string[] = [];
  if (match.localMatchday?.name) descParts.push(`Spieltag: ${match.localMatchday.name}`);
  if (match.notes) descParts.push(`Notizen: ${match.notes}`);

  return {
    id: match.id,
    title,
    startDate,
    endDate,
    location: match.location,
    description: descParts.length > 0 ? descParts.join('\n') : undefined,
  };
}

